import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Spinner } from "../../shared/ui";
import { decodeArrayBufferWithBOM } from "./TextUtils";

import remarkGfm from "remark-gfm";

const MarkdownViewer = ({ fileUrl }) => {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!fileUrl) {
      setMarkdown("No file URL provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    fetch(fileUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => setMarkdown(decodeArrayBufferWithBOM(arrayBuffer)))
      .catch((error) => {
        console.error("Failed to fetch or decode markdown file:", error);
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [fileUrl]);

  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" {...props}>
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-gray-100 text-ink px-1.5 py-0.5 rounded font-mono text-sm" {...props}>
          {children}
        </code>
      );
    },
    table({ node, ...props }) {
      return (
        <div className="overflow-x-auto my-6 border border-line rounded-lg">
          <table className="min-w-full divide-y divide-line m-0" {...props} />
        </div>
      );
    },
    th({ node, ...props }) {
      return (
        <th 
          className="px-4 py-3 bg-gray-50 text-ink font-semibold text-left text-sm whitespace-nowrap border-r border-line last:border-r-0 m-0"
          {...props} 
        />
      );
    },
    td({ node, ...props }) {
      return (
        <td 
          className="px-4 py-3 text-sm text-ink border-r border-line last:border-r-0 m-0 break-words min-w-[150px] max-w-[500px]"
          {...props} 
        />
      );
    },
    tr({ node, ...props }) {
      return <tr className="divide-x divide-line m-0 border-b border-line last:border-b-0" {...props} />;
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full w-full py-10">
        <Spinner />
      </div>
    );
  }

  if (loadFailed) {
    return <div className="text-status-critical font-semibold p-4">Could not load the file content.</div>;
  }

  return (
    <div className="w-full flex flex-col bg-gray-50 h-[70vh] overflow-y-auto border border-line rounded-lg">
      <div className="w-full min-h-full p-8 text-sm text-ink bg-white">
        <div className="prose max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{markdown}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

MarkdownViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
};

export default MarkdownViewer;
